/**
 * Tab time controls — selectable-option builders and current-active-state
 * derivation. Split out of tab-time-controls.ts (the compatibility engine) to
 * keep each file focused and under the line limit; re-exported from there so
 * consumers keep importing from "@utils/tab-time-controls".
 */
import type {
  GuardrailRollingOption,
  TimeSeriesViewerOptions,
  PointInTimeViewerOptions,
  TimeSeriesViewerUnitConfig,
  PointInTimeViewerPeriodConfig,
  TimeSeriesUnit,
  ViewerSelectableTimePeriod,
} from "@models/Dataset";
import type { WidgetViewerStateMap } from "@hooks/use-widget-rolling-average";
import type {
  TabTimeFamilyAnalysis,
  TabTimeFamilyKind,
  SharedTimeConfig,
} from "./tab-time-controls";

/** Named periods a viewer can actually select (matches the per-widget popover). */
const VIEWER_SELECTABLE_PERIODS: ReadonlySet<string> = new Set([
  "latest_month",
  "previous_month",
  "latest_quarter",
  "previous_quarter",
  "latest_semester",
  "previous_semester",
]);

// ─── Selectable-option builders (mirror the per-widget ViewerTimePopover) ──────

function dedupeRollingOptions(
  values: GuardrailRollingOption[],
): GuardrailRollingOption[] {
  const seen = new Set<number>();
  const out: GuardrailRollingOption[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/** The primary selector's options: time units (trend) or named periods (point-in-time). */
export function getPrimaryOptions(
  family: TabTimeFamilyAnalysis,
  savedDefault: string,
): string[] {
  const vo = family.members[0]?.viewerOptions;
  if (family.kind === "trend") {
    const additional = (vo as TimeSeriesViewerOptions | null)?.additionalUnits ?? {};
    return [
      savedDefault,
      ...Object.keys(additional).filter((u) => u !== savedDefault),
    ];
  }
  const additional =
    (vo as PointInTimeViewerOptions | null)?.additionalPeriods ?? {};
  return [
    ...(VIEWER_SELECTABLE_PERIODS.has(savedDefault) ? [savedDefault] : []),
    ...Object.keys(additional).filter((p) => p !== savedDefault),
  ];
}

/** Rolling-average options available for a given active unit/period. */
export function getRollingOptions(
  family: TabTimeFamilyAnalysis,
  savedDefault: string,
  savedRA: GuardrailRollingOption,
  active: string,
): GuardrailRollingOption[] {
  const vo = family.members[0]?.viewerOptions;
  if (family.kind === "trend") {
    const ts = vo as TimeSeriesViewerOptions | null;
    if (active === savedDefault) {
      return dedupeRollingOptions([
        savedRA,
        ...(ts?.chartUnitAdditionalRollingAverages ?? []),
      ]);
    }
    return (
      ts?.additionalUnits?.[active as TimeSeriesUnit]?.allowedRollingAverages ??
      []
    );
  }
  const pit = vo as PointInTimeViewerOptions | null;
  const additional = pit?.additionalPeriods?.[active as ViewerSelectableTimePeriod];
  if (active === savedDefault || !additional) {
    return dedupeRollingOptions([
      savedRA,
      ...(pit?.chartPeriodAdditionalRollingAverages ?? []),
    ]);
  }
  return additional.allowedRollingAverages ?? [];
}

/** The rolling average applied when the family first switches to a unit/period. */
export function getDefaultRollingForPrimary(
  family: TabTimeFamilyAnalysis,
  savedDefault: string,
  savedRA: GuardrailRollingOption,
  primary: string,
): GuardrailRollingOption {
  if (primary === savedDefault) return savedRA;
  const vo = family.members[0]?.viewerOptions;
  if (family.kind === "trend") {
    return (
      (vo as TimeSeriesViewerOptions | null)?.additionalUnits?.[
        primary as TimeSeriesUnit
      ]?.defaultRollingAverage ?? 0
    );
  }
  return (
    (vo as PointInTimeViewerOptions | null)?.additionalPeriods?.[
      primary as ViewerSelectableTimePeriod
    ]?.defaultRollingAverage ?? 0
  );
}

// ─── Shared-contract summary (for the editor alignment callout) ────────────────

export interface SharedContractOption {
  /** A time unit (trend) or named period (point-in-time). */
  value: string;
  /** True for the researcher-saved default unit/period. */
  isDefault: boolean;
  /** Rolling-average options offered for this unit/period. */
  rollingAverages: GuardrailRollingOption[];
}

/**
 * Flatten a shared tab-time config into the units/periods it exposes and the
 * rolling-average options per unit/period. Mirrors getPrimaryOptions +
 * getRollingOptions, but reads the config directly (no family analysis needed).
 */
export function summarizeSharedContract(
  config: SharedTimeConfig,
  kind: TabTimeFamilyKind,
): SharedContractOption[] {
  const { savedDefault, savedRollingAverage: savedRA, viewerOptions: vo } =
    config;

  if (kind === "trend") {
    const ts = vo as TimeSeriesViewerOptions | null;
    const additional = ts?.additionalUnits ?? {};
    const values = [
      savedDefault,
      ...Object.keys(additional).filter((u) => u !== savedDefault),
    ];
    return values.map((value) => ({
      value,
      isDefault: value === savedDefault,
      rollingAverages:
        value === savedDefault
          ? dedupeRollingOptions([
              savedRA,
              ...(ts?.chartUnitAdditionalRollingAverages ?? []),
            ])
          : (additional[value as TimeSeriesUnit]?.allowedRollingAverages ?? []),
    }));
  }

  const pit = vo as PointInTimeViewerOptions | null;
  const additional = pit?.additionalPeriods ?? {};
  const values = [
    ...(VIEWER_SELECTABLE_PERIODS.has(savedDefault) ? [savedDefault] : []),
    ...Object.keys(additional).filter((p) => p !== savedDefault),
  ];
  return values.map((value) => {
    const cfg = additional[value as ViewerSelectableTimePeriod];
    return {
      value,
      isDefault: value === savedDefault,
      rollingAverages:
        value === savedDefault || !cfg
          ? dedupeRollingOptions([
              savedRA,
              ...(pit?.chartPeriodAdditionalRollingAverages ?? []),
            ])
          : (cfg.allowedRollingAverages ?? []),
    };
  });
}

// ─── Adopt a shared contract onto the chart being edited (Module 3) ────────────

/**
 * The result of trying to adopt a shared contract onto the chart currently
 * being configured.
 *   - "ready"   → the chart's saved unit/period is one of the contract's options;
 *                 `viewerOptions` is the contract re-rooted onto that saved unit/
 *                 period (copies only the contract — never the saved unit/period
 *                 or the chart's own rolling average). `alignsDefault` is false
 *                 when the saved unit/period differs from the siblings' saved
 *                 default (the tab control will stay Mixed until that is aligned).
 *   - "blocked" → the saved unit/period is not one of the contract's options.
 */
export type TabTimeAdoption =
  | {
      status: "ready";
      viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions;
      alignsDefault: boolean;
    }
  | { status: "blocked" };

/** Units/periods the contract represents (the base default plus the extras). */
function adoptableSettings(
  config: SharedTimeConfig,
  kind: TabTimeFamilyKind,
): Set<string> {
  const additional =
    kind === "trend"
      ? (config.viewerOptions as TimeSeriesViewerOptions | null)?.additionalUnits
      : (config.viewerOptions as PointInTimeViewerOptions | null)
          ?.additionalPeriods;
  return new Set<string>([config.savedDefault, ...Object.keys(additional ?? {})]);
}

/** A uniform unit→config map covering every unit the contract exposes. */
function trendUnitConfigs(
  config: SharedTimeConfig,
): Map<string, TimeSeriesViewerUnitConfig> {
  const ts = config.viewerOptions as TimeSeriesViewerOptions | null;
  const map = new Map<string, TimeSeriesViewerUnitConfig>();
  map.set(config.savedDefault, {
    allowedRollingAverages: dedupeRollingOptions([
      config.savedRollingAverage,
      ...(ts?.chartUnitAdditionalRollingAverages ?? []),
    ]),
    defaultRollingAverage: config.savedRollingAverage,
  });
  for (const [unit, cfg] of Object.entries(ts?.additionalUnits ?? {})) {
    if (cfg) map.set(unit, cfg);
  }
  return map;
}

function periodConfigs(
  config: SharedTimeConfig,
): Map<string, PointInTimeViewerPeriodConfig> {
  const pit = config.viewerOptions as PointInTimeViewerOptions | null;
  const map = new Map<string, PointInTimeViewerPeriodConfig>();
  map.set(config.savedDefault, {
    allowedRollingAverages: dedupeRollingOptions([
      config.savedRollingAverage,
      ...(pit?.chartPeriodAdditionalRollingAverages ?? []),
    ]),
    defaultRollingAverage: config.savedRollingAverage,
  });
  for (const [period, cfg] of Object.entries(pit?.additionalPeriods ?? {})) {
    if (cfg) map.set(period, cfg);
  }
  return map;
}

/**
 * Determine whether the chart being edited can adopt `config`, and if so build
 * the viewer-time options re-rooted onto its saved `savedSetting` (leaving that
 * saved unit/period and the chart's own `savedRA` untouched).
 */
export function getTabTimeAdoption(
  config: SharedTimeConfig,
  kind: TabTimeFamilyKind,
  savedSetting: string | null,
  savedRA: GuardrailRollingOption,
): TabTimeAdoption {
  if (!savedSetting || !adoptableSettings(config, kind).has(savedSetting)) {
    return { status: "blocked" };
  }
  const alignsDefault = savedSetting === config.savedDefault;

  if (kind === "trend") {
    const configs = trendUnitConfigs(config);
    const chartCfg = configs.get(savedSetting);
    const additionalUnits: Partial<
      Record<TimeSeriesUnit, TimeSeriesViewerUnitConfig>
    > = {};
    for (const [unit, cfg] of configs) {
      if (unit !== savedSetting) additionalUnits[unit as TimeSeriesUnit] = cfg;
    }
    return {
      status: "ready",
      alignsDefault,
      viewerOptions: {
        enabled: true,
        chartUnitAdditionalRollingAverages: (
          chartCfg?.allowedRollingAverages ?? []
        ).filter((ra) => ra !== savedRA),
        additionalUnits,
      },
    };
  }

  const configs = periodConfigs(config);
  const chartCfg = configs.get(savedSetting);
  const additionalPeriods: Partial<
    Record<ViewerSelectableTimePeriod, PointInTimeViewerPeriodConfig>
  > = {};
  for (const [period, cfg] of configs) {
    // Only viewer-selectable periods can be represented as extras (e.g. the
    // siblings' saved default may be all_time, which cannot be an extra).
    if (period !== savedSetting && VIEWER_SELECTABLE_PERIODS.has(period)) {
      additionalPeriods[period as ViewerSelectableTimePeriod] = cfg;
    }
  }
  return {
    status: "ready",
    alignsDefault,
    viewerOptions: {
      enabled: true,
      chartPeriodAdditionalRollingAverages: (
        chartCfg?.allowedRollingAverages ?? []
      ).filter((ra) => ra !== savedRA),
      additionalPeriods,
    },
  };
}

// ─── Current active state (from per-widget viewer overrides) ───────────────────

export interface FamilyActiveState {
  /** Uniform active unit/period across the family, or null if it varies. */
  activeDefault: string | null;
  /** Uniform active rolling average across the family, or null if it varies. */
  activeRollingAverage: GuardrailRollingOption | null;
  /** True when any family member has a viewer override applied. */
  hasOverride: boolean;
}

/**
 * Fold each member's per-widget viewer override (falling back to the shared
 * saved default) into a single family-level active state. A field is null when
 * members disagree — i.e. it "varies by visualization".
 */
export function deriveFamilyActiveState(
  family: TabTimeFamilyAnalysis,
  viewerStates: WidgetViewerStateMap,
  savedDefault: string,
  savedRA: GuardrailRollingOption,
): FamilyActiveState {
  // The currently-selectable options (already guardrail-filtered). A URL
  // override pointing at an option that guardrails have since removed is stale
  // and must be ignored — otherwise the control would keep showing a value that
  // is no longer offered (and the per-widget clock has already dropped it).
  const primaryOptions = getPrimaryOptions(family, savedDefault);

  let activeDefault: string | null = null;
  let activeRollingAverage: GuardrailRollingOption | null = null;
  let hasOverride = false;
  let first = true;

  for (const member of family.members) {
    const state = viewerStates[member.widgetId];
    const rawDefault =
      family.kind === "trend" ? state?.timeUnit : state?.timePeriod;
    const validDefault =
      rawDefault && primaryOptions.includes(rawDefault) ? rawDefault : null;
    const memberDefault = validDefault ?? savedDefault;

    const raOptions = getRollingOptions(
      family,
      savedDefault,
      savedRA,
      memberDefault,
    );
    const rawRA = state?.rollingAverage;
    const validRA =
      rawRA !== null && rawRA !== undefined && raOptions.includes(rawRA)
        ? rawRA
        : null;
    const memberRA = validRA ?? savedRA;

    // Only a still-selectable override counts — stale ones are treated as "no override".
    if (validDefault !== null || validRA !== null) hasOverride = true;

    if (first) {
      activeDefault = memberDefault;
      activeRollingAverage = memberRA;
      first = false;
    } else {
      if (memberDefault !== activeDefault) activeDefault = null;
      if (memberRA !== activeRollingAverage) activeRollingAverage = null;
    }
  }

  // Once the primary varies, the rolling average is meaningless at family level.
  if (activeDefault === null) activeRollingAverage = null;

  return { activeDefault, activeRollingAverage, hasOverride };
}
